import cc from "classcat"
import React from "react"
import { Table as ReactTable } from "react-table"

import styles from "./Row.module.css"

const RowContainer = ({
  children,
  row,
  style,
  rowRef,
  href,
  className,
}: {
  children: React.ReactNode
  row: any
  style?: Record<string, string | number>
  rowRef: React.Ref<HTMLDivElement>
  href?: string
  className?: string
}) =>
  href ? (
    <a
      {...row.getRowProps({
        style,
      })}
      className={cc([styles.tr, className])}
      ref={rowRef}
      href={href}
    >
      {children}
    </a>
  ) : (
    <div
      {...row.getRowProps({
        style,
      })}
      className={cc([styles.tr, className])}
      ref={rowRef}
    >
      {children}
    </div>
  )

const Row = ({
  table,
  row,
  style,
  setSize,
  index,
  cellClassName = undefined,
  href = undefined,
  className = undefined,
  evenRowClassName,
}: {
  index: number
  table: ReactTable
  row: any
  style?: Record<string, string | number>
  setSize: (index: number, size: number) => void
  cellClassName?: (cellId: string) => string | undefined
  className?: string
  href?: string
  evenRowClassName?: string
}) => {
  table.prepareRow(row)

  const rowRef = React.useRef<HTMLDivElement>(null)

  const prevSize = React.useRef<number | undefined>(undefined)

  // we need to compute the height of the row constantly in order to react to changes
  // in the row's content. The prevSize is used to prevent infinite rendering loops.
  React.useEffect(() => {
    if (rowRef.current) {
      const children = Array.from(rowRef.current.children)

      const largestChild = children.reduce((largest, child) => {
        if (
          child.getBoundingClientRect().height >
          largest.getBoundingClientRect().height
        ) {
          return child
        }

        return largest
      }, children[0])

      const height = largestChild.getBoundingClientRect().height

      if (height === prevSize.current) {
        return
      }

      prevSize.current = height

      setSize(index, height)
    }
  })

  return (
    <RowContainer
      row={row}
      style={style}
      rowRef={rowRef}
      className={cc([className, evenRowClassName && index % 2 === 1])}
      href={href}
    >
      {row.cells.map((cell) => (
        <div
          {...cell.getCellProps({
            style: {
              minWidth: cell.column.minWidth,
              width: cell.column.width,
            },
          })}
          className={
            styles.td +
            (cellClassName ? ` ${cellClassName(cell.column.id)}` : "")
          }
        >
          {cell.render("Cell")}
        </div>
      ))}
    </RowContainer>
  )
}

export default Row
